interface EnvironmentConfig {
  api: {
    baseUrl: string;
  };
  app: {
    mapConstantId: number;
    mapboxToken: string;
    posthogKey?: string;
    muiLicenseKey?: string;
    posthogHost?: string;
    email?: string;
  };
  development: {
    isDev: boolean;
    isProduction: boolean;
  };
}

const parseNumber = (value: string | undefined, varName: string): number => {
  if (!value) {
    throw new TypeError(
      `Environment variable ${varName} is required but not set`
    );
  }

  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    throw new TypeError(
      `Environment variable ${varName} must be a valid number, got: ${value}`
    );
  }

  return parsed;
};

const parseString = (value: string | undefined, varName: string): string => {
  if (!value) {
    throw new TypeError(
      `Environment variable ${varName} is required but not set`
    );
  }

  return value.trim();
};

const parseOptionalString = (value: string | undefined): string | undefined => {
  return value?.trim() || undefined;
};

export const env: EnvironmentConfig = {
  api: {
    baseUrl: parseString(process.env.REACT_APP_API, "REACT_APP_API"),
  },
  app: {
    mapConstantId: parseNumber(
      process.env.REACT_APP_CONSTANT_ID_MAP,
      "REACT_APP_CONSTANT_ID_MAP"
    ),
    mapboxToken: parseString(
      process.env.REACT_APP_MAPBOX_TOKEN,
      "REACT_APP_MAPBOX_TOKEN"
    ),
    posthogKey: parseOptionalString(process.env.REACT_APP_POSTHOG_KEY),
    muiLicenseKey: parseOptionalString(process.env.REACT_APP_KEY_UI),
    posthogHost: parseOptionalString(process.env.REACT_APP_PUBLIC_POSTHOG),
    email: parseOptionalString(process.env.REACT_APP_EMAIL),
  },
  development: {
    isDev: process.env.NODE_ENV === "development",
    isProduction: process.env.NODE_ENV === "production",
  },
};

export const validateEnvironment = (): void => {
  try {
    console.log("🔍 Raw environment variables:", {
      REACT_APP_API: process.env.REACT_APP_API,
      REACT_APP_CONSTANT_ID: process.env.REACT_APP_CONSTANT_ID,
      REACT_APP_CONSTANT_ID_MAP: process.env.REACT_APP_CONSTANT_ID_MAP,
      REACT_APP_MAPBOX_TOKEN: process.env.REACT_APP_MAPBOX_TOKEN
        ? "***present***"
        : "missing",
      NODE_ENV: process.env.NODE_ENV,
    });

    if (env.development.isDev) {
      console.log("🔧 Development mode - Environment variables:", {
        apiUrl: env.api.baseUrl,
        mapConstantId: env.app.mapConstantId,

        mapboxToken: env.app.mapboxToken ? "***configured***" : "missing",
        posthogKey: env.app.posthogKey ? "***configured***" : "not set",
        muiLicenseKey: env.app.muiLicenseKey ? "***configured***" : "not set",
        posthogHost: env.app.posthogHost || "not set",
        email: env.app.email || "using default",
      });
    }
  } catch (error) {
    console.error("❌ Environment configuration validation failed:", error);
    console.error(
      "🔍 Available environment variables:",
      Object.keys(process.env).filter((key) => key.startsWith("REACT_APP_"))
    );
    throw error;
  }
};

export default env;
